import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CalendarRange,
  ChevronDown,
  ChevronRight,
  Copy,
  Loader2,
  Users,
  Wallet,
  Hash,
  ExternalLink,
} from "lucide-react";
import { fetchPayrollHub, fetchPayrollRunDetail } from "./payrollApi";
import OrganisationDisplayField from "../../components/common/OrganisationDisplayField";
import BrixigoPageLoader from "../../components/common/BrixigoPageLoader";
import { usePayrollWorkflow } from "./payrollWorkflow";
import {
  formatMoney,
  formatMonthLabel,
  resolveRunStatus,
  periodStatusBadge,
} from "./payrollRunUtils";

function ContextLabel({ label, display, id }) {
  if (!display && !id) return null;
  const copy = () => id && navigator.clipboard?.writeText(String(id));
  return (
    <div className="flex items-center gap-1 text-xs text-slate-600">
      <span className="text-slate-400">{label}:</span>
      <span className="font-medium text-slate-800" title={id || undefined}>
        {display || "—"}
      </span>
      {id ? (
        <button type="button" onClick={copy} className="p-0.5 hover:text-indigo-600" title="Copy ID">
          <Copy className="h-3 w-3" />
        </button>
      ) : null}
    </div>
  );
}

function RunDetailPanel({ runId, onUseRun }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    fetchPayrollRunDetail(runId)
      .then((res) => {
        if (!cancelled) setDetail(res.data);
      })
      .catch((e) => {
        if (!cancelled) setError(e?.response?.data?.detail || e.message || "Failed to load");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [runId]);

  if (loading) {
    return <BrixigoPageLoader compact message="Loading payroll breakdown…" fullPage={false} />;
  }
  if (error) {
    return <p className="text-sm text-red-600 py-4">{error}</p>;
  }
  if (!detail) return null;

  const { run, pay_period, summary, employees } = detail;
  const totals = summary?.totals || {};
  const st = resolveRunStatus(run);

  return (
    <div className="border-t border-slate-100 bg-slate-50/80 p-4 sm:p-6 space-y-5">
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="rounded-xl bg-white border border-slate-200 p-4">
          <p className="text-xs text-slate-500 uppercase tracking-wide">Gross pay</p>
          <p className="text-xl font-semibold text-slate-900 mt-1">₹{formatMoney(run.gross_pay_total)}</p>
        </div>
        <div className="rounded-xl bg-white border border-slate-200 p-4">
          <p className="text-xs text-slate-500 uppercase tracking-wide">Net pay</p>
          <p className="text-xl font-semibold text-emerald-700 mt-1">₹{formatMoney(run.net_pay_total)}</p>
        </div>
        <div className="rounded-xl bg-white border border-slate-200 p-4">
          <p className="text-xs text-slate-500 uppercase tracking-wide">Deductions</p>
          <p className="text-xl font-semibold text-red-700 mt-1">₹{formatMoney(totals.deductions)}</p>
        </div>
        <div className="rounded-xl bg-white border border-slate-200 p-4">
          <p className="text-xs text-slate-500 uppercase tracking-wide">Employees</p>
          <p className="text-xl font-semibold text-slate-900 mt-1">{employees?.length ?? 0}</p>
        </div>
      </div>

      <div className="flex flex-wrap gap-4 text-xs">
        <ContextLabel
          label="Payroll run"
          display={pay_period?.period_label ? `${pay_period.period_label} (${st.label})` : st.label}
          id={run.payroll_run_id}
        />
        <ContextLabel
          label="Pay period"
          display={pay_period?.period_label}
          id={pay_period?.pay_period_id}
        />
        <span className={`inline-flex px-2 py-0.5 rounded-full border text-xs font-medium ${st.className}`}>
          {st.label}
        </span>
      </div>

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => onUseRun(run)}
          className="inline-flex items-center gap-1 px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700"
        >
          Process payroll <ExternalLink className="h-3.5 w-3.5" />
        </button>
      </div>

      {employees?.length > 0 ? (
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs text-slate-500 uppercase">
              <tr>
                <th className="p-3">Employee</th>
                <th className="p-3 text-right">Earnings</th>
                <th className="p-3 text-right">Deductions</th>
                <th className="p-3 text-right">Net payable</th>
                <th className="p-3 text-right">Payable days</th>
              </tr>
            </thead>
            <tbody>
              {employees.map((emp) => (
                <tr key={emp.employee_id} className="border-t border-slate-100 hover:bg-slate-50/50">
                  <td className="p-3">
                    <div className="font-medium text-slate-800">{emp.employee_name}</div>
                    {emp.employee_code ? <div className="text-xs text-slate-400">{emp.employee_code}</div> : null}
                  </td>
                  <td className="p-3 text-right tabular-nums">₹{formatMoney(emp.earnings)}</td>
                  <td className="p-3 text-right tabular-nums text-red-700">₹{formatMoney(emp.deductions)}</td>
                  <td className="p-3 text-right tabular-nums font-semibold text-emerald-800">
                    ₹{formatMoney(emp.net_salary)}
                  </td>
                  <td className="p-3 text-right text-slate-600">{emp.payable_days ?? "—"}</td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-slate-50 font-semibold border-t border-slate-200">
              <tr>
                <td className="p-3">Total ({employees.length})</td>
                <td className="p-3 text-right">₹{formatMoney(totals.earnings)}</td>
                <td className="p-3 text-right">₹{formatMoney(totals.deductions)}</td>
                <td className="p-3 text-right text-emerald-800">₹{formatMoney(totals.net)}</td>
                <td className="p-3" />
              </tr>
            </tfoot>
          </table>
        </div>
      ) : (
        <p className="text-sm text-slate-500 py-4 text-center rounded-lg border border-dashed border-slate-200 bg-white">
          No payroll lines yet — process this run to calculate employee amounts.
        </p>
      )}
    </div>
  );
}

export default function PayrollRunsHub() {
  const navigate = useNavigate();
  const { organisationId, organisationName, setWorkflow } = usePayrollWorkflow();
  const [hub, setHub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [expandedPeriod, setExpandedPeriod] = useState(null);
  const [expandedRun, setExpandedRun] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetchPayrollHub();
      setHub(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Failed to load payroll history");
      setHub(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const stats = useMemo(() => {
    const periods = hub?.periods || [];
    let totalNet = 0;
    let latestRun = null;
    for (const p of periods) {
      for (const r of p.runs || []) {
        totalNet += Number(r.net_pay_total || 0);
        if (!latestRun || new Date(r.created_at) > new Date(latestRun.created_at)) {
          latestRun = r;
        }
      }
    }
    return {
      periodCount: periods.length,
      runCount: hub?.total_runs ?? 0,
      totalNet,
      latestRun,
    };
  }, [hub]);

  const selectRun = (run, period) => {
    setWorkflow({
      organisationId: run.organisation_id,
      organisationName,
      payPeriodId: run.pay_period_id,
      payPeriodLabel: period.period_label,
      payrollRunId: run.payroll_run_id,
      payrollRunLabel: run.period_label || "Payroll Run",
      payPeriodStart: period.start_date,
      payPeriodEnd: period.end_date,
    });
  };

  const togglePeriod = (periodId) => {
    setExpandedPeriod((cur) => (cur === periodId ? null : periodId));
    setExpandedRun(null);
  };

  const toggleRun = (runId, period) => {
    setExpandedRun((cur) => (cur === runId ? null : runId));
    if (runId) {
      selectRun(
        period.runs.find((r) => r.payroll_run_id === runId) || { organisation_id: organisationId },
        period
      );
    }
  };

  return (
    <div className="w-full max-w-full min-w-0 px-4 sm:px-6 py-6">
      <OrganisationDisplayField
        organisationId={organisationId}
        organisationName={organisationName}
        className="mb-6 max-w-md"
        hint="All periods and runs below belong to this organisation."
      />

      {loading && (
        <BrixigoPageLoader compact message="Loading payroll history…" />
      )}

      {error && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800 mb-6">{error}</div>
      )}

      {!loading && hub && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-8">
            <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex items-center gap-2 text-slate-500 text-xs font-medium uppercase">
                <CalendarRange className="h-4 w-4" /> Pay periods
              </div>
              <p className="text-2xl font-bold text-slate-900 mt-2">{stats.periodCount}</p>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex items-center gap-2 text-slate-500 text-xs font-medium uppercase">
                <Hash className="h-4 w-4" /> Payroll runs
              </div>
              <p className="text-2xl font-bold text-slate-900 mt-2">{stats.runCount}</p>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex items-center gap-2 text-slate-500 text-xs font-medium uppercase">
                <Wallet className="h-4 w-4" /> Combined net
              </div>
              <p className="text-2xl font-bold text-emerald-700 mt-2">₹{formatMoney(stats.totalNet)}</p>
            </div>
            <div className="rounded-xl border border-indigo-100 bg-indigo-50/50 p-4 shadow-sm">
              <p className="text-xs text-indigo-600 font-medium uppercase">Quick action</p>
              <button
                type="button"
                onClick={() => navigate("/payroll-runs/new")}
                className="mt-2 w-full py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700"
              >
                + New payroll run
              </button>
            </div>
          </div>

          {hub.periods.length === 0 ? (
            <div className="text-center py-16 rounded-2xl border border-dashed border-slate-300 bg-white">
              <p className="text-slate-600 font-medium">No pay periods yet</p>
              <p className="text-sm text-slate-500 mt-2 max-w-md mx-auto">
                Create a pay period first, then start a payroll run for that month.
              </p>
              <button
                type="button"
                onClick={() => navigate("/pay-periods")}
                className="mt-4 px-5 py-2 rounded-lg bg-indigo-600 text-white text-sm"
              >
                Create pay period
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">
                Pay periods by month
              </h2>
              {hub.periods.map((period) => {
                const isOpen = expandedPeriod === period.pay_period_id;
                const pst = periodStatusBadge(period.status);
                const monthLabel = formatMonthLabel(period.month_key);

                return (
                  <div
                    key={period.pay_period_id}
                    className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden"
                  >
                    <button
                      type="button"
                      onClick={() => togglePeriod(period.pay_period_id)}
                      className="w-full flex items-center gap-3 p-4 text-left hover:bg-slate-50 transition"
                    >
                      {isOpen ? (
                        <ChevronDown className="h-5 w-5 text-indigo-600 shrink-0" />
                      ) : (
                        <ChevronRight className="h-5 w-5 text-slate-400 shrink-0" />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-semibold text-slate-900">{monthLabel}</span>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${pst.className}`}>{pst.label}</span>
                          <span className="text-xs text-slate-500">{period.period_label}</span>
                        </div>
                        <p className="text-xs text-slate-400 mt-1">
                          {period.run_count} run(s)
                        </p>
                      </div>
                      <div className="text-right shrink-0 hidden sm:block">
                        <p className="text-xs text-slate-500">Runs</p>
                        <p className="text-lg font-bold text-slate-800">{period.run_count}</p>
                      </div>
                    </button>

                    {isOpen && (
                      <div className="border-t border-slate-100">
                        {period.runs.length === 0 ? (
                          <p className="p-6 text-sm text-slate-500 text-center">
                            No payroll runs for this period.{" "}
                            <button
                              type="button"
                              className="text-indigo-600 underline"
                              onClick={() => {
                                setWorkflow({
                                  organisationId,
                                  organisationName,
                                  payPeriodId: period.pay_period_id,
                                  payPeriodLabel: period.period_label,
                                  payPeriodStart: period.start_date,
                                  payPeriodEnd: period.end_date,
                                });
                                navigate("/payroll-runs/new");
                              }}
                            >
                              Create one
                            </button>
                          </p>
                        ) : (
                          <div className="divide-y divide-slate-100">
                            {period.runs.map((run) => {
                              const st = resolveRunStatus(run);
                              const runOpen = expandedRun === run.payroll_run_id;
                              return (
                                <div key={run.payroll_run_id}>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      if (runOpen) {
                                        setExpandedRun(null);
                                      } else {
                                        setExpandedRun(run.payroll_run_id);
                                        selectRun(run, period);
                                      }
                                    }}
                                    className={`w-full flex flex-wrap items-center gap-3 px-4 py-3 text-left hover:bg-indigo-50/30 ${
                                      runOpen ? "bg-indigo-50/40" : ""
                                    }`}
                                  >
                                    <div className="flex-1 min-w-[12rem]">
                                      <div className="flex items-center gap-2">
                                        <span className="font-medium text-slate-800">
                                          {period.period_label || "Payroll run"}
                                        </span>
                                        <span
                                          className={`text-xs px-2 py-0.5 rounded-full border ${st.className}`}
                                        >
                                          {st.label}
                                        </span>
                                      </div>
                                      <p className="text-xs text-slate-400 mt-0.5">
                                        Created {new Date(run.created_at).toLocaleString()}
                                      </p>
                                    </div>
                                    <div className="flex items-center gap-4 text-sm">
                                      <span className="flex items-center gap-1 text-slate-600">
                                        <Users className="h-3.5 w-3.5" />
                                        {run.employee_count}
                                      </span>
                                      <span className="font-semibold text-emerald-700 tabular-nums">
                                        ₹{formatMoney(run.net_pay_total)}
                                      </span>
                                    </div>
                                    {runOpen ? (
                                      <ChevronDown className="h-4 w-4 text-indigo-600" />
                                    ) : (
                                      <ChevronRight className="h-4 w-4 text-slate-400" />
                                    )}
                                  </button>
                                  {runOpen && (
                                    <RunDetailPanel
                                      runId={run.payroll_run_id}
                                      onUseRun={(r) => {
                                        selectRun(run, period);
                                        navigate("/process-payroll");
                                      }}
                                    />
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}
